/**
 * ============================================================================
 * Maintenance Context Provider
 * ============================================================================
 * React Context for managing maintenance schedules and jobs state
 * 
 * Pattern: Follows existing context conventions (HoursContext, ClientContext)
 * State Management: Centralized state for maintenance features
 * API Integration: All backend calls abstracted here
 * 
 * Usage:
 *   import { MaintenanceContext } from './contexts/MaintenanceContext';
 *   const { jobs, schedules, loadJobs, completeJob } = useContext(MaintenanceContext);
 * ============================================================================
 */

import React, { createContext, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { ScreenContext } from './ScreenContext';

export const MaintenanceContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

const MaintenanceContextProvider = (props) => {
  const { token, logout } = useContext(AuthContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  // ============================================================================
  // State
  // ============================================================================
  const [isLoading, setIsLoading] = useState(false);
  
  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  
  // Assets state
  const [assets, setAssets] = useState([]);
  // Client emails for notification selection
  const [clientEmails, setClientEmails] = useState([]);
  
  // Stats state (for admin dashboard)
  const [stats, setStats] = useState(null);

  // ============================================================================
  // Error Handler
  // ============================================================================
  const handleError = (error, defaultMessage) => {
    console.error('[MaintenanceContext] Error:', error);
    
    if (error.response?.status === 401) {
      setSnackbarMessage({
        message: 'Session expired. Please log in again.',
        severity: 'error'
      });
      logout();
    } else {
      const errorMessage = error.response?.data?.error || defaultMessage;
      setSnackbarMessage({
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // ============================================================================
  // Jobs API Functions
  // ============================================================================

  /**
   * Load jobs with optional filters
   * @param {Object} filters - { status, from_date, to_date }
   */
  const loadJobs = useCallback(async (filters = {}) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `${API_URL}api/maintenance/jobs${params ? '?' + params : ''}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setJobs(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load maintenance jobs');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Load upcoming jobs (next 30 days)
   */
  const loadUpcomingJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/jobs/upcoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUpcomingJobs(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load upcoming jobs');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Load a single job with full details
   * @param {number} jobId
   */
  const loadJobDetails = useCallback(async (jobId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedJob(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load job details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Start a job (mark as in progress)
   * @param {number} jobId
   */
  const startJob = useCallback(async (jobId) => {
    setIsLoading(true);
    try {
      await axios.put(
        `${API_URL}api/maintenance/jobs/${jobId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: 'Job started successfully',
        severity: 'success'
      });
      
      // Refresh job details if it's the selected job
      if (selectedJob && selectedJob.job && selectedJob.job.id === jobId) {
        await loadJobDetails(jobId);
      }
      
      return true;
    } catch (error) {
      handleError(error, 'Failed to start job');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedJob, loadJobDetails, setSnackbarMessage]);

  /**
   * Complete a job
   * @param {number} jobId
   * @param {Object} data - { completion_notes, actual_downtime_minutes }
   */
  const completeJob = useCallback(async (jobId, data) => {
    setIsLoading(true);
    try {
      await axios.put(
        `${API_URL}api/maintenance/jobs/${jobId}/complete`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: 'Job completed successfully',
        severity: 'success'
      });
      
      // Refresh job details if it's the selected job
      if (selectedJob && selectedJob.job && selectedJob.job.id === jobId) {
        await loadJobDetails(jobId);
      }
      
      return true;
    } catch (error) {
      handleError(error, 'Failed to complete job');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedJob, loadJobDetails, setSnackbarMessage]);

  /**
   * Skip a job
   * @param {number} jobId
   * @param {string} reason - Required reason for skipping
   */
  const skipJob = useCallback(async (jobId, reason) => {
    setIsLoading(true);
    try {
      await axios.put(
        `${API_URL}api/maintenance/jobs/${jobId}/skip`,
        { completion_notes: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: 'Job skipped',
        severity: 'info'
      });
      
      // Refresh job details if it's the selected job
      if (selectedJob && selectedJob.job && selectedJob.job.id === jobId) {
        await loadJobDetails(jobId);
      }
      
      return true;
    } catch (error) {
      handleError(error, 'Failed to skip job');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedJob, loadJobDetails, setSnackbarMessage]);

  

  // ============================================================================
  // Schedules API Functions
  // ============================================================================

  /**
   * Load all schedules (admins see all, users see their assigned)
   */
  const loadSchedules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSchedules(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load maintenance schedules');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Load a single schedule with its jobs
   * @param {number} scheduleId
   */
  const loadScheduleDetails = useCallback(async (scheduleId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedSchedule(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load schedule details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Manually notify client for a job
   * @param {number} jobId
   */
  const notifyClient = useCallback(async (jobId) => {
    setIsLoading(true);
    try {
      const resp = await axios.put(
        `${API_URL}api/maintenance/jobs/${jobId}/notify-client`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const recipient = resp.data?.recipient || resp.data?.to || '';
      setSnackbarMessage({
        message: `Client notification recorded${recipient ? ` to ${recipient}` : ''}`,
        severity: 'success'
      });

      // Refresh currently selected job or schedule details if open
      if (selectedJob && selectedJob.job && selectedJob.job.id === jobId) {
        await loadJobDetails(jobId);
      }
      if (selectedSchedule && selectedSchedule.schedule) {
        await loadScheduleDetails(selectedSchedule.schedule.id);
      }

      return true;
    } catch (error) {
      handleError(error, 'Failed to notify client');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedJob, selectedSchedule, loadJobDetails, loadScheduleDetails, setSnackbarMessage]);

  /**
   * Create a new schedule
   * @param {Object} scheduleData - Schedule configuration
   */
  const createSchedule = useCallback(async (scheduleData) => {
    setIsLoading(true);
    try {
      // Normalize keys from UI to backend shape (idempotent if already normalized)
      const payload = { ...scheduleData };
      if (payload.requires_client_notification !== undefined) {
        payload.notify_client = !!payload.requires_client_notification;
        delete payload.requires_client_notification;
      }
      if (payload.estimated_downtime_minutes !== undefined) {
        payload.expected_downtime_minutes = payload.estimated_downtime_minutes === '' ? null : parseInt(payload.estimated_downtime_minutes, 10);
        delete payload.estimated_downtime_minutes;
      }
      if (payload.is_active !== undefined && !payload.status) {
        payload.status = payload.is_active ? 'active' : 'paused';
        delete payload.is_active;
      }
      const response = await axios.post(
        `${API_URL}api/maintenance/schedules`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: 'Schedule created successfully',
        severity: 'success'
      });
      
      // Refresh schedules list
      await loadSchedules();
      
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to create schedule');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, loadSchedules, setSnackbarMessage]);

  /**
   * Update a schedule
   * @param {number} scheduleId
   * @param {Object} updates - Fields to update
   */
  const updateSchedule = useCallback(async (scheduleId, updates) => {
    setIsLoading(true);
    try {
      const payload = { ...updates };
      if (payload.requires_client_notification !== undefined) {
        payload.notify_client = !!payload.requires_client_notification;
        delete payload.requires_client_notification;
      }
      if (payload.estimated_downtime_minutes !== undefined) {
        payload.expected_downtime_minutes = payload.estimated_downtime_minutes === '' ? null : parseInt(payload.estimated_downtime_minutes, 10);
        delete payload.estimated_downtime_minutes;
      }
      if (payload.is_active !== undefined && !payload.status) {
        payload.status = payload.is_active ? 'active' : 'paused';
        delete payload.is_active;
      }
      await axios.put(
        `${API_URL}api/maintenance/schedules/${scheduleId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      
      setSnackbarMessage({
        message: 'Schedule updated successfully',
        severity: 'success'
      });
      
      // Refresh schedule details if it's the selected schedule
      if (selectedSchedule && selectedSchedule.schedule && selectedSchedule.schedule.id === scheduleId) {
        await loadScheduleDetails(scheduleId);
      }
      
      return true;
    } catch (error) {
      handleError(error, 'Failed to update schedule');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedSchedule, loadScheduleDetails, setSnackbarMessage]);

  /**
   * Delete (archive) a schedule
   * @param {number} scheduleId
   */
  const deleteSchedule = useCallback(async (scheduleId) => {
    setIsLoading(true);
    try {
      await axios.delete(`${API_URL}api/maintenance/schedules/${scheduleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSnackbarMessage({
        message: 'Schedule archived successfully',
        severity: 'success'
      });
      
      // Refresh schedules list
      await loadSchedules();
      
      return true;
    } catch (error) {
      handleError(error, 'Failed to archive schedule');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token, loadSchedules, setSnackbarMessage]);

  // ============================================================================
  // Assets API Functions
  // ============================================================================

  /**
   * Load maintenance assets
   * @param {number} clientId - Optional: filter by client
   */
  const loadAssets = useCallback(async (clientId = null) => {
    setIsLoading(true);
    try {
      const params = clientId ? `?client_id=${clientId}` : '';
      const response = await axios.get(`${API_URL}api/maintenance/assets${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssets(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load maintenance assets');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  /**
   * Create a new asset
   * @param {Object} assetData
   */
  const createAsset = useCallback(async (assetData) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}api/maintenance/assets`,
        assetData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: 'Asset created successfully',
        severity: 'success'
      });
      
      // Refresh assets list
      await loadAssets();
      
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to create asset');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, loadAssets, setSnackbarMessage]);

  /**
   * Load client emails (primary + contacts) for notification selection
   */
  const loadClientEmails = useCallback(async (clientId) => {
    if (!clientId) {
      setClientEmails([]);
      return [];
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/client-emails?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientEmails(response.data.emails || []);
      return response.data.emails || [];
    } catch (error) {
      handleError(error, 'Failed to load client emails');
      setClientEmails([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // ============================================================================
  // Admin Functions
  // ============================================================================

  /**
   * Manually trigger job generation (admin only)
   */
  const generateJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}api/maintenance/generate-jobs`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbarMessage({
        message: `Jobs generated successfully. ${response.data.jobsCreated || 0} jobs created.`,
        severity: 'success'
      });
      
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to generate jobs');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, setSnackbarMessage]);

  /**
   * Load maintenance statistics (admin only)
   */
  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}api/maintenance/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(response.data);
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to load statistics');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, setSnackbarMessage]);

  // ============================================================================
  // Context Value
  // ============================================================================
  const value = {
    // State
    isLoading,
    jobs,
    upcomingJobs,
    selectedJob,
    schedules,
    selectedSchedule,
    assets,
    clientEmails,
    stats,
    
    // Jobs functions
    loadJobs,
    loadUpcomingJobs,
    loadJobDetails,
    startJob,
    completeJob,
    skipJob,
    notifyClient,
    
    // Schedules functions
    loadSchedules,
    loadScheduleDetails,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    
    // Assets functions
    loadAssets,
    createAsset,
    // Emails
    loadClientEmails,
    
    // Admin functions
    generateJobs,
    loadStats,
    
    // State setters (for manual state management if needed)
    setSelectedJob,
    setSelectedSchedule
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {props.children}
    </MaintenanceContext.Provider>
  );
};

export default MaintenanceContextProvider;
